import { Box, Stack, Typography } from '@mui/material';
import { Controller, FieldValues, Path } from 'react-hook-form';

import { FormAwareInputProps } from '@/components/form/formAwareInputs/FormAwareInput.types';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';

function validateColorFormat(value: string | undefined | null) {
  if (!value) {
    return undefined;
  }

  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexRegex.test(value) ? undefined : 'Color must be hex (e.g. #FFFFFF)';
}

export interface SlideConfigColorInputProps<TFormShape extends FieldValues> {
  control: FormAwareInputProps<TFormShape>['control'];
  heading: React.ReactNode;
  subheading: React.ReactNode;
  fieldName: Path<TFormShape>;
  colorValue: string;
  placeholder?: string;
  disabled?: boolean;
}

export function SlideConfigColorInput<TFormShape extends FieldValues>({
  heading,
  subheading,
  fieldName,
  control,
  colorValue,
  placeholder = '#000000',
  disabled,
}: SlideConfigColorInputProps<TFormShape>) {
  // Native color inputs require six digits; the text input also accepts shorthand.
  const pickerColor = /^#[0-9a-f]{3}$/i.test(colorValue)
    ? `#${colorValue
        .slice(1)
        .split('')
        .map((digit) => digit.repeat(2))
        .join('')}`
    : colorValue;
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
    >
      <Stack
        width={`calc(100% - 100px)`}
        direction="row"
        alignItems="center"
        justifyContent="flex-start"
        spacing={1}
      >
        <Controller
          control={control}
          name={fieldName}
          render={({ field }) => (
            <Box
              component="input"
              type="color"
              aria-label={`${heading} picker`}
              value={
                /^#[0-9a-f]{6}$/i.test(pickerColor) ? pickerColor : '#FFFFFF'
              }
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                cursor: 'pointer',
                p: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            />
          )}
        />
        <Box>
          <Typography variant="title-s" component="div">
            {heading}
          </Typography>
          <Typography variant="body-xs" component="div">
            {subheading}
          </Typography>
        </Box>
      </Stack>
      <Box maxWidth={100}>
        <FormAwareTextInput<TFormShape>
          validation={{
            format: validateColorFormat,
          }}
          fieldName={fieldName}
          control={control}
          hideLabel
          label={`${heading}`}
          placeholder={placeholder}
          disabled={disabled}
        />
      </Box>
    </Stack>
  );
}
